import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/client';
import {
  Bell, Mail, MessageSquare, Smartphone, Clock,
  AlertTriangle, RefreshCw, Zap, UserCheck, Radio,
  Save, Check, ExternalLink
} from 'lucide-react';

const priorityColors = {
  normal: 'bg-slate-100 text-slate-600',
  high: 'bg-amber-100 text-amber-700',
  urgent: 'bg-red-100 text-red-700',
  critical: 'bg-red-100 text-red-700'
};

const channelIcons = {
  push: Smartphone,
  email: Mail,
  sms: MessageSquare
};

const Toggle = ({ checked, onChange, disabled }) => (
  <button
    onClick={() => !disabled && onChange(!checked)}
    disabled={disabled}
    className={`relative w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary/30 ${
      checked ? 'bg-primary' : 'bg-slate-300'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${checked ? 'translate-x-5' : 'translate-x-0'}`} />
  </button>
);

const SystemNotifications = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [saved, setSaved] = useState({});
  const [quietStart, setQuietStart] = useState('22:00');
  const [quietEnd, setQuietEnd] = useState('08:00');
  const [overrideForUrgent, setOverrideForUrgent] = useState(true); // always default true for patient safety
  const [quietDirty, setQuietDirty] = useState(false);

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/settings/notifications/config');
      if (res.data?.success) {
        setConfig(res.data.data);
        setQuietStart(res.data.data.quietHours?.start || '22:00');
        setQuietEnd(res.data.data.quietHours?.end || '08:00');
        setOverrideForUrgent(res.data.data.quietHours?.overrideForUrgent ?? true);
        setQuietDirty(false);
      }
    } catch (err) {
      console.error('Failed to load notification config:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const toggleChannel = async (channel, enabled) => {
    setSaving(s => ({ ...s, [`ch-${channel}`]: true }));
    try {
      await api.put('/admin/settings/notifications/channel', { channel, enabled });
      setConfig(prev => ({
        ...prev,
        channels: { ...prev.channels, [channel]: { ...prev.channels[channel], enabled } }
      }));
      flashSaved(`ch-${channel}`);
    } catch (err) {
      console.error('Failed to toggle channel:', err);
    } finally {
      setSaving(s => ({ ...s, [`ch-${channel}`]: false }));
    }
  };

  const toggleTemplate = async (templateKey, enabled) => {
    setSaving(s => ({ ...s, [`tpl-${templateKey}`]: true }));
    try {
      await api.put('/admin/settings/notifications/template', { templateKey, enabled });
      setConfig(prev => ({
        ...prev,
        templates: prev.templates.map(t => t.key === templateKey ? { ...t, enabled } : t)
      }));
      flashSaved(`tpl-${templateKey}`);
    } catch (err) {
      console.error('Failed to toggle template:', err);
    } finally {
      setSaving(s => ({ ...s, [`tpl-${templateKey}`]: false }));
    }
  };

  const saveQuietHours = async () => {
    setSaving(s => ({ ...s, quietHours: true }));
    try {
      await api.put('/admin/settings/notifications/quiet-hours', { start: quietStart, end: quietEnd, overrideForUrgent });
      setConfig(prev => ({ ...prev, quietHours: { start: quietStart, end: quietEnd, overrideForUrgent } }));
      setQuietDirty(false);
      flashSaved('quietHours');
    } catch (err) {
      console.error('Failed to save quiet hours:', err);
    } finally {
      setSaving(s => ({ ...s, quietHours: false }));
    }
  };

  const flashSaved = (key) => {
    setSaved(s => ({ ...s, [key]: true }));
    setTimeout(() => setSaved(s => ({ ...s, [key]: false })), 2000);
  };

  if (loading) {
    return (
      <div className="space-y-6 py-6">
        <div className="h-8 bg-slate-200 rounded w-1/3 animate-pulse" />
        <div className="space-y-4">
          {[1, 2, 3].map(i => <div key={i} className="h-32 bg-slate-100 rounded-2xl animate-pulse" />)}
        </div>
      </div>
    );
  }

  const escalationLevels = config?.escalation?.levels || [];
  const escalationContacts = config?.escalation?.contacts || [];
  const onCallNow = config?.escalation?.onCallNow;

  return (
    <div className="space-y-6 py-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-headline text-2xl font-extrabold text-slate-900 tracking-tight">System Notifications</h2>
          <p className="text-slate-500 text-sm mt-1">Configure notification channels, templates, and escalation</p>
        </div>
        <button onClick={loadConfig} className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-600 hover:bg-slate-50 transition-all">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Channels — toggle on/off */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-5 border-b border-slate-100">
          <h3 className="font-bold text-lg text-slate-900">Notification Channels</h3>
          <p className="text-xs text-slate-500 mt-1">Enable or disable notification delivery channels</p>
        </div>
        <div className="divide-y divide-slate-100">
          {Object.entries(config?.channels || {}).map(([key, channel]) => {
            const Icon = channelIcons[key] || Bell;
            const isSaving = saving[`ch-${key}`];
            const isSaved = saved[`ch-${key}`];
            return (
              <div key={key} className="flex items-center gap-4 px-5 py-4">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${channel.enabled ? 'bg-primary/10 text-primary' : 'bg-slate-100 text-slate-500'}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-bold text-slate-900 capitalize">{key}</p>
                  <p className="text-xs text-slate-500">Provider: {channel.provider}</p>
                </div>
                <span className={`text-xs font-bold px-2 py-1 rounded-full ${channel.configured ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                  {channel.configured ? 'Configured' : 'Not Configured'}
                </span>
                <div className="flex items-center gap-2">
                  <Toggle checked={channel.enabled} onChange={(v) => toggleChannel(key, v)} disabled={isSaving} />
                  {isSaved && <Check className="w-4 h-4 text-emerald-500" />}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Escalation Rules */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-5 border-b border-slate-100">
          <h3 className="font-bold text-lg text-slate-900 flex items-center gap-2">
            <Zap className="w-5 h-5 text-primary" /> Escalation Rules
          </h3>
          <p className="text-xs text-slate-500 mt-1">Time-based escalation stages from the escalation service</p>
        </div>
        {escalationLevels.length === 0 ? (
          <p className="p-5 text-sm text-slate-500 text-center">No escalation rules configured.</p>
        ) : (
          <div className="p-5 space-y-2">
            {escalationLevels.map((level, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className={`text-xs font-bold px-2 py-1 rounded-full shrink-0 uppercase ${priorityColors[level.priority] || priorityColors.normal}`}>
                  {level.priority}
                </span>
                <div className="flex-1 p-3 bg-slate-50 rounded-xl">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-bold text-slate-900">{level.action}</p>
                      <p className="text-xs text-slate-500">Stage: {level.stage}</p>
                    </div>
                    <span className="text-xs font-bold text-primary flex items-center gap-1 shrink-0">
                      <Clock className="w-3 h-3" /> {level.delayLabel}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Escalation Contacts */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-lg text-slate-900 flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-primary" /> Escalation Contacts
            </h3>
            <p className="text-xs text-slate-500 mt-1">{escalationContacts.length} active contact{escalationContacts.length !== 1 ? 's' : ''}</p>
          </div>
          <div className="flex items-center gap-2">
            {onCallNow && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 rounded-lg">
                <Radio className="w-3 h-3 text-emerald-600 animate-pulse" />
                <span className="text-xs font-bold text-emerald-700">On-call: {onCallNow.name}</span>
              </div>
            )}
            <button
              onClick={() => navigate('/admin/on-call')}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 text-primary rounded-lg text-xs font-bold hover:bg-primary/20 transition-all"
            >
              <ExternalLink className="w-3 h-3" /> Manage Contacts
            </button>
          </div>
        </div>
        {escalationContacts.length === 0 ? (
          <p className="p-5 text-sm text-slate-500 text-center">No escalation contacts configured.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {escalationContacts.map((contact, i) => (
              <div key={contact._id || i} className="flex items-center gap-4 px-5 py-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-900">{contact.name || 'Unknown'}</p>
                  <p className="text-xs text-slate-500">{contact.email} · {contact.escalationRole?.replace(/_/g, ' ')}</p>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  {(contact.notificationChannels || []).map(ch => {
                    const ChIcon = channelIcons[ch] || Bell;
                    return (
                      <div key={ch} className="w-6 h-6 rounded bg-slate-100 flex items-center justify-center" title={ch}>
                        <ChIcon className="w-3 h-3 text-slate-500" />
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-1 shrink-0">
                  {(contact.levels || []).map(lvl => (
                    <span key={lvl} className="text-xs font-bold px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded">{lvl}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Notification Templates — toggle on/off */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-5 border-b border-slate-100">
          <h3 className="font-bold text-lg text-slate-900">Notification Templates</h3>
          <p className="text-xs text-slate-500 mt-1">
            {(config?.templates || []).length} templates · toggle to enable or disable each
          </p>
        </div>
        {(config?.templates || []).length === 0 ? (
          <p className="p-5 text-sm text-slate-500 text-center">No templates found.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {(config?.templates || []).map((template) => {
              const isSaving = saving[`tpl-${template.key}`];
              const isSaved = saved[`tpl-${template.key}`];
              return (
                <div key={template.key} className="flex items-center gap-4 px-5 py-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-slate-900">{template.name}</p>
                    <p className="text-xs text-slate-500 font-mono">{template.key}</p>
                  </div>
                  <span className={`text-xs font-bold px-2 py-1 rounded-full shrink-0 ${priorityColors[template.priority] || priorityColors.normal}`}>
                    {template.priority}
                  </span>
                  <div className="flex gap-1.5 shrink-0">
                    {(template.channels || []).map(ch => {
                      const ChIcon = channelIcons[ch] || Bell;
                      return (
                        <div key={ch} className="w-6 h-6 rounded bg-slate-100 flex items-center justify-center" title={ch}>
                          <ChIcon className="w-3 h-3 text-slate-500" />
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex items-center gap-2">
                    <Toggle checked={template.enabled} onChange={(v) => toggleTemplate(template.key, v)} disabled={isSaving} />
                    {isSaved && <Check className="w-4 h-4 text-emerald-500" />}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Quiet Hours — editable */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-lg text-slate-900 flex items-center gap-2">
            <Clock className="w-5 h-5 text-primary" /> Quiet Hours
          </h3>
          {quietDirty && (
            <button
              onClick={saveQuietHours}
              disabled={saving.quietHours}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-xl text-sm font-bold hover:bg-primary/90 transition-all disabled:opacity-50"
            >
              {saving.quietHours ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save
            </button>
          )}
          {saved.quietHours && (
            <span className="flex items-center gap-1 text-xs font-bold text-emerald-600">
              <Check className="w-4 h-4" /> Saved
            </span>
          )}
        </div>
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <label className="block text-xs text-slate-500 font-bold uppercase mb-1">Start</label>
            <input
              type="time"
              value={quietStart}
              onChange={(e) => { setQuietStart(e.target.value); setQuietDirty(true); }}
              className="w-full px-4 py-3 bg-slate-50 rounded-xl text-lg font-extrabold text-slate-900 text-center border border-slate-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
            />
          </div>
          <span className="text-slate-300 font-bold mt-5">→</span>
          <div className="flex-1">
            <label className="block text-xs text-slate-500 font-bold uppercase mb-1">End</label>
            <input
              type="time"
              value={quietEnd}
              onChange={(e) => { setQuietEnd(e.target.value); setQuietDirty(true); }}
              className="w-full px-4 py-3 bg-slate-50 rounded-xl text-lg font-extrabold text-slate-900 text-center border border-slate-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
            />
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between gap-4 p-3 bg-slate-50 rounded-xl border border-slate-200">
          <div>
            <p className="text-sm font-bold text-slate-800">Override for urgent &amp; critical</p>
            <p className="text-xs text-slate-500 mt-0.5">Urgent and critical notifications will bypass quiet hours</p>
          </div>
          <Toggle
            checked={overrideForUrgent}
            onChange={(v) => { setOverrideForUrgent(v); setQuietDirty(true); }}
          />
        </div>
      </div>
    </div>
  );
};

export default SystemNotifications;
